import React from 'react'
import { Box } from '@mui/material'
import { ArrowTrendingUpIcon, MagnifyingGlassIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS } from '../../../constants/colors'
import { useTranslation } from '../../hooks/useIntractionTranslate'
import { SprintPurposeHeader } from './SprintPurposeHeader'
import { SprintPurposeContent } from './SprintPurposeContent'
import { SprintPurposeFooter } from './SprintPurposeFooter'
import type { SprintPurposeIcon, SprintPurposeProps } from './SprintPurpose.types'

const PURPOSE_ICON_MAPPING: Record<string, SprintPurposeIcon> = {
  aware: ArrowTrendingUpIcon,
  train: MagnifyingGlassIcon,
  custom: UserGroupIcon
}
export const SprintPurpose: React.FC<SprintPurposeProps> = ({
  purposes,
  selectedPurpose,
  onPurposeSelect,
  className = ''
}) => {
  const { t } = useTranslation()

  return (
    <Box
      className={className}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
        gap: '24px'
      }}
    >
      {purposes.map((purpose) => {
        const IconComponent = PURPOSE_ICON_MAPPING[purpose.id] ?? purpose.icon ?? ArrowTrendingUpIcon
        const selected = selectedPurpose === purpose.id
        const hideFooter = purpose.id === 'custom'

        return (
          <Box
            key={purpose.id}
            onClick={() => onPurposeSelect(purpose.id)}
            data-testid={`purpose-card-${purpose.id}`}
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              p: '32px',
              borderRadius: '16px',
              border: '1px solid',
              borderColor: selected ? INTERAX_COLORS.sun : 'rgba(255, 255, 255, 0.08)',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.01)',
                boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.25)'
              },
              ...(selected
                ? {
                    boxShadow: `0 0 0 2px ${INTERAX_COLORS.sun}`,
                    background: INTERAX_COLORS.sun20
                  }
                : {})
            }}
          >
            <SprintPurposeHeader icon={IconComponent} selected={selected} />
            <SprintPurposeContent purpose={purpose} translate={t} />
            <Box sx={{ mt: 'auto', width: '100%' }}>
              {!hideFooter && <SprintPurposeFooter metrics={purpose.metrics} selected={selected} />}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export default SprintPurpose
